import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  UserPlus, Users, Gift, Link, Copy, RefreshCw, Send, Check, X,
  TrendingUp, Award, Settings2, Save, ChevronRight, Star, Crown
} from "lucide-react";

export default function ReferralPage() {
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("dashboard");
  const [showGenerate, setShowGenerate] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [clients, setClients] = useState([]);
  const [genClient, setGenClient] = useState("");
  const [submitForm, setSubmitForm] = useState({ code: "", referee_name: "", referee_email: "", referee_phone: "" });
  const [configForm, setConfigForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, refRes, codeRes, cliRes] = await Promise.all([
        api.get("/referrals/dashboard"),
        api.get("/referrals"),
        api.get("/referrals/codes"),
        api.get("/clients?limit=200"),
      ]);
      setDashboard(dashRes.data);
      setReferrals(refRes.data || []);
      setCodes(codeRes.data || []);
      setClients((cliRes.data?.clients || cliRes.data || []));
      setConfigForm(dashRes.data?.config || {});
    } catch { toast.error("Failed to load referral data"); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const generateCode = async () => {
    if (!genClient) { toast.error("Select a client"); return; }
    try {
      const res = await api.post("/referrals/codes", { client_id: genClient });
      toast.success(`Code generated: ${res.data.code}`);
      setShowGenerate(false);
      setGenClient("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitReferral = async () => {
    if (!submitForm.code || !submitForm.referee_name) { toast.error("Code and name required"); return; }
    try {
      await api.post("/referrals/submit", submitForm);
      toast.success("Referral submitted!");
      setShowSubmit(false);
      setSubmitForm({ code: "", referee_name: "", referee_email: "", referee_phone: "" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const convertReferral = async (refId) => {
    try {
      const res = await api.post(`/referrals/${refId}/convert`);
      toast.success(`Converted! ${res.data.referrer_points} pts to referrer, ${res.data.referee_points} pts to referee`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/referrals/config", configForm);
      toast.success("Settings saved");
      setShowConfig(false);
      fetchAll();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  const s = dashboard?.summary || {};
  const config = dashboard?.config || {};

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  return (
    <div data-testid="referral-page" className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Referral Program</h1>
          <p className="text-sm text-slate-400 mt-1">Reward clients for bringing friends — {config.referrer_points || 500} pts per referral</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowConfig(!showConfig)} className="border-slate-200 text-slate-600" data-testid="config-btn">
            <Settings2 className="w-4 h-4 mr-1.5" /> Settings
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowSubmit(true)} className="border-slate-200 text-slate-600" data-testid="submit-referral-btn">
            <Send className="w-4 h-4 mr-1.5" /> Use Code
          </Button>
          <Button size="sm" onClick={() => setShowGenerate(true)} className="bg-slate-900 text-white" data-testid="generate-code-btn">
            <Link className="w-4 h-4 mr-1.5" /> Generate Code
          </Button>
        </div>
      </div>

      {/* Config Panel */}
      {showConfig && (
        <div data-testid="config-panel" className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <h3 className="font-['Outfit'] font-semibold text-slate-900">Referral Settings</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Referrer Points</label>
              <Input type="number" value={configForm.referrer_points || 500} onChange={e => setConfigForm(f => ({ ...f, referrer_points: +e.target.value }))} className="border-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Referee Points</label>
              <Input type="number" value={configForm.referee_points || 250} onChange={e => setConfigForm(f => ({ ...f, referee_points: +e.target.value }))} className="border-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Referrer Reward</label>
              <Input value={configForm.referrer_reward || ""} onChange={e => setConfigForm(f => ({ ...f, referrer_reward: e.target.value }))} className="border-slate-200" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Referee Reward</label>
              <Input value={configForm.referee_reward || ""} onChange={e => setConfigForm(f => ({ ...f, referee_reward: e.target.value }))} className="border-slate-200" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveConfig} disabled={saving} className="bg-slate-900 text-white" data-testid="save-config-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}</Button>
            <Button size="sm" variant="outline" onClick={() => setShowConfig(false)} className="border-slate-200">Cancel</Button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div data-testid="stat-total" className="bg-white border border-slate-200 rounded-2xl p-4">
          <Users className="w-5 h-5 text-[#8B5CF6] mb-2" />
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{s.total_referrals || 0}</p>
          <p className="text-xs text-slate-400">Total Referrals</p>
        </div>
        <div data-testid="stat-converted" className="bg-white border border-slate-200 rounded-2xl p-4">
          <Check className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="font-['Outfit'] text-xl font-semibold text-emerald-600">{s.converted || 0}</p>
          <p className="text-xs text-slate-400">Converted</p>
        </div>
        <div data-testid="stat-pending" className="bg-white border border-slate-200 rounded-2xl p-4">
          <RefreshCw className="w-5 h-5 text-[#856404] mb-2" />
          <p className="font-['Outfit'] text-xl font-semibold text-[#856404]">{s.pending || 0}</p>
          <p className="text-xs text-slate-400">Pending</p>
        </div>
        <div data-testid="stat-rate" className="bg-white border border-slate-200 rounded-2xl p-4">
          <TrendingUp className="w-5 h-5 text-[#EC4899] mb-2" />
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{s.conversion_rate || 0}%</p>
          <p className="text-xs text-slate-400">Conversion Rate</p>
        </div>
        <div data-testid="stat-codes" className="bg-white border border-slate-200 rounded-2xl p-4">
          <Link className="w-5 h-5 text-[#F59E0B] mb-2" />
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{s.active_codes || 0}</p>
          <p className="text-xs text-slate-400">Active Codes</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[{ key: "dashboard", label: "Overview" }, { key: "codes", label: "Referral Codes" }, { key: "referrals", label: "All Referrals" }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-rose-300"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {tab === "dashboard" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* How it works */}
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#333] rounded-2xl p-6 text-white">
            <h3 className="font-['Outfit'] font-semibold text-lg mb-4">How It Works</h3>
            <div className="space-y-4">
              {[
                { step: "1", title: "Generate Code", desc: "Create a unique referral code for any client" },
                { step: "2", title: "Client Shares", desc: "Client shares their code with friends and family" },
                { step: "3", title: "Friend Books", desc: "New client uses the code when booking their first visit" },
                { step: "4", title: "Both Rewarded", desc: `Referrer gets ${config.referrer_points || 500} pts, friend gets ${config.referee_points || 250} pts` },
              ].map(s => (
                <div key={s.step} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold flex-shrink-0">{s.step}</div>
                  <div>
                    <p className="text-sm font-medium">{s.title}</p>
                    <p className="text-xs text-white/60">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Referrers */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 mb-4 flex items-center gap-2"><Crown className="w-4 h-4 text-[#F59E0B]" /> Top Referrers</h3>
            {(dashboard?.top_referrers || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No referrals yet</p>
            ) : (
              <div className="space-y-3">
                {(dashboard?.top_referrers || []).map((r, i) => (
                  <div key={i} data-testid={`top-referrer-${i}`} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-[#F59E0B] text-white" : i === 1 ? "bg-[#C0C0C0] text-white" : i === 2 ? "bg-[#CD7F32] text-white" : "bg-slate-50 text-slate-600"}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-400">{r.count} referrals, {r.converted} converted</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-[#F59E0B]" />
                      <span className="text-sm font-semibold text-slate-900">{r.converted * (config.referrer_points || 500)}</span>
                      <span className="text-xs text-slate-400">pts</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reward Summary */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-2">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 mb-3">Reward Structure</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#FAFAFA] rounded-lg p-4 text-center">
                <Gift className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
                <p className="font-['Outfit'] font-semibold text-slate-900">{config.referrer_points || 500} pts</p>
                <p className="text-xs text-slate-400 mt-0.5">For the referrer</p>
                <p className="text-xs text-slate-600 mt-1">{config.referrer_reward || ""}</p>
              </div>
              <div className="bg-[#FAFAFA] rounded-lg p-4 text-center">
                <UserPlus className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="font-['Outfit'] font-semibold text-slate-900">{config.referee_points || 250} pts</p>
                <p className="text-xs text-slate-400 mt-0.5">For the new client</p>
                <p className="text-xs text-slate-600 mt-1">{config.referee_reward || ""}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Codes Tab */}
      {tab === "codes" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-[#FAFAF9]">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Client</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Code</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Uses</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Conversions</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.code_id} data-testid={`code-row-${c.code_id}`} className="border-b border-slate-100 hover:bg-[#FAFAF9]">
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">{c.client_name}</td>
                  <td className="py-3 px-4 text-center">
                    <code className="bg-[#FAFAFA] text-slate-900 px-3 py-1 rounded-lg font-mono text-sm font-semibold tracking-wider border border-slate-200">{c.code}</code>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-slate-600">{c.uses}</td>
                  <td className="py-3 px-4 text-center text-sm font-semibold text-emerald-600">{c.conversions}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.is_active ? "bg-[#D4EDDA] text-[#155724]" : "bg-slate-50 text-slate-400"}`}>
                      {c.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Button variant="ghost" size="sm" onClick={() => copyCode(c.code)} className="text-slate-600" data-testid={`copy-${c.code_id}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Referrals Tab */}
      {tab === "referrals" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-[#FAFAF9]">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Referrer</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Referred</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Code</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Points</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map(r => (
                <tr key={r.referral_id} data-testid={`ref-row-${r.referral_id}`} className="border-b border-slate-100 hover:bg-[#FAFAF9]">
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">{r.referrer_name}</td>
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-slate-900">{r.referee_name}</p>
                    <p className="text-xs text-slate-400">{r.referee_email}</p>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <code className="text-xs font-mono text-slate-600">{r.code}</code>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${r.status === "converted" ? "bg-[#D4EDDA] text-[#155724]" : "bg-[#FEF3CD] text-[#856404]"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm">
                    {r.status === "converted" ? (
                      <span className="text-emerald-600 font-semibold">{r.referrer_points_awarded + r.referee_points_awarded} pts</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {r.status === "pending" && (
                      <Button size="sm" onClick={() => convertReferral(r.referral_id)} className="bg-emerald-600 text-white text-xs" data-testid={`convert-${r.referral_id}`}>
                        <Check className="w-3 h-3 mr-1" /> Convert
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate Code Modal */}
      {showGenerate && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowGenerate(false)}>
          <div data-testid="generate-modal" className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-['Outfit'] font-semibold text-slate-900">Generate Referral Code</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowGenerate(false)}><X className="w-4 h-4" /></Button>
            </div>
            <p className="text-sm text-slate-400">Select a client to generate their unique referral code.</p>
            <Select value={genClient} onValueChange={setGenClient}>
              <SelectTrigger className="border-slate-200" data-testid="gen-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={generateCode} className="w-full bg-slate-900 text-white" data-testid="gen-submit-btn">Generate Code</Button>
          </div>
        </div>
      )}

      {/* Submit Referral Modal */}
      {showSubmit && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowSubmit(false)}>
          <div data-testid="submit-modal" className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-['Outfit'] font-semibold text-slate-900">Submit a Referral</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowSubmit(false)}><X className="w-4 h-4" /></Button>
            </div>
            <p className="text-sm text-slate-400">Enter the referral code and new client details.</p>
            <Input value={submitForm.code} onChange={e => setSubmitForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="Referral Code (e.g. OLMA1234)" className="border-slate-200 font-mono" data-testid="submit-code-input" />
            <Input value={submitForm.referee_name} onChange={e => setSubmitForm(f => ({ ...f, referee_name: e.target.value }))} placeholder="New Client Name *" className="border-slate-200" data-testid="submit-name-input" />
            <Input value={submitForm.referee_email} onChange={e => setSubmitForm(f => ({ ...f, referee_email: e.target.value }))} placeholder="Email" className="border-slate-200" />
            <Input value={submitForm.referee_phone} onChange={e => setSubmitForm(f => ({ ...f, referee_phone: e.target.value }))} placeholder="Phone" className="border-slate-200" />
            <Button onClick={submitReferral} className="w-full bg-slate-900 text-white" data-testid="submit-ref-btn">Submit Referral</Button>
          </div>
        </div>
      )}
    </div>
  );
}
